import { PropallyAgentV1Api } from "./generated/src";
import type { UserSearchEnvelopeRequestType } from "../types/UsersSearch";
import type { ScheduledProposalDateOption } from "../utils/proposalSchedule";

export type ScheduledProposalKind = "investment" | "assessment";

export type ScheduledProposalConflict = {
  conflictCount: number;
  conflictIds: number[];
};

type ScheduledDatesResponseBody = {
  scheduled_dates?: { proposal_date?: unknown; count?: unknown }[];
};

type SuperAgentResponse = { body?: unknown };

/**
 * scheduled_dates のレスポンスから予約日時の選択肢を組み立てる。
 *
 * 生成クライアントは proposal_date を JS の Date に変換してしまい、サーバーが返した
 * iso8601(6) のマイクロ秒が落ちる。一括取り消しは proposal_date の完全一致で消すため、
 * 変換済みの data ではなく **素の response.body** から文字列のまま取り出す。
 */
export const extractScheduledDateOptions = (body: unknown): ScheduledProposalDateOption[] => {
  const scheduledDates = (body as ScheduledDatesResponseBody | null | undefined)?.scheduled_dates;
  if (!Array.isArray(scheduledDates)) return [];

  return scheduledDates.reduce<ScheduledProposalDateOption[]>((options, item) => {
    const rawProposalDate = item?.proposal_date;
    if (typeof rawProposalDate !== "string" || rawProposalDate.length === 0) return options;

    const count = typeof item?.count === "number" ? item.count : 0;
    options.push({ rawProposalDate, count });
    return options;
  }, []);
};

// 予約日時ごとの件数一覧を取得する
export const fetchScheduledProposalDates = async (
  kind: ScheduledProposalKind
): Promise<ScheduledProposalDateOption[]> => {
  return new Promise((resolve, reject) => {
    const api = new PropallyAgentV1Api();
    const call =
      kind === "investment"
        ? api.agentV1InvestmentProposalsScheduledDatesGet.bind(api)
        : api.agentV1AssessmentProposalsScheduledDatesGet.bind(api);

    call((error: object, data: unknown, response: SuperAgentResponse) => {
      if (error) {
        console.error(error);
        reject(error);
      } else {
        resolve(extractScheduledDateOptions(response?.body));
      }
    });
  });
};

// 指定した予約日時の予約をすべて取り消す。rawProposalDate は scheduled_dates が返した文字列をそのまま渡す
export const cancelScheduledProposalsAt = async (
  kind: ScheduledProposalKind,
  rawProposalDate: string
): Promise<number> => {
  return new Promise((resolve, reject) => {
    const api = new PropallyAgentV1Api();
    const call =
      kind === "investment"
        ? api.agentV1InvestmentProposalsScheduledCancellationsPost.bind(api)
        : api.agentV1AssessmentProposalsScheduledCancellationsPost.bind(api);

    call({ proposal_date: rawProposalDate }, (error: object, data: { canceled_count?: number }) => {
      if (error) {
        console.error(error);
        reject(error);
      } else {
        resolve(data?.canceled_count ?? 0);
      }
    });
  });
};

// 同じ日（JST）に既存の送信予約があるユーザーを数える
export const fetchScheduledInvestmentConflicts = async (
  userIds: number[],
  proposalDate: string
): Promise<ScheduledProposalConflict> => {
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1InvestmentProposalsScheduledConflictsPost(
      { user_ids: userIds, proposal_date: proposalDate },
      (error: object, data: { conflict_user_count?: number; conflict_user_ids?: number[] }) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          resolve({ conflictCount: data?.conflict_user_count ?? 0, conflictIds: data?.conflict_user_ids ?? [] });
        }
      }
    );
  });
};

/**
 * 絞り込み条件（search）で送る一括提案の重複件数を数える。
 *
 * 対象ユーザーは送信時にサーバー側で確定するため、ID を持たないまま
 * **送信に使うのと同じエンベロープと re_proposal** を渡し、母集団・上限まで
 * bulk と同じロジックで解決してもらう（ここがズレると確認した件数と実際に置き換わる件数が食い違う）。
 */
export const fetchScheduledInvestmentConflictsBySearch = async (
  search: UserSearchEnvelopeRequestType,
  proposalDate: string,
  isReProposal: boolean
): Promise<ScheduledProposalConflict> => {
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1InvestmentProposalsScheduledConflictsPost(
      { target_type: "search", search, re_proposal: isReProposal, proposal_date: proposalDate },
      (error: object, data: { conflict_user_count?: number; conflict_user_ids?: number[] }) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          resolve({ conflictCount: data?.conflict_user_count ?? 0, conflictIds: data?.conflict_user_ids ?? [] });
        }
      }
    );
  });
};

// 同じ日（JST）に既存の送信予約がある物件を数える
export const fetchScheduledAssessmentConflicts = async (
  propertyIds: number[],
  proposalDate: string
): Promise<ScheduledProposalConflict> => {
  return new Promise((resolve, reject) => {
    new PropallyAgentV1Api().agentV1AssessmentProposalsScheduledConflictsPost(
      { property_ids: propertyIds, proposal_date: proposalDate },
      (error: object, data: { conflict_property_count?: number; conflict_property_ids?: number[] }) => {
        if (error) {
          console.error(error);
          reject(error);
        } else {
          resolve({
            conflictCount: data?.conflict_property_count ?? 0,
            conflictIds: data?.conflict_property_ids ?? [],
          });
        }
      }
    );
  });
};
